'use client'
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import { AppBar, Box, Divider, Drawer, IconButton, List, ListItemButton, ListItemIcon, ListItemText, Toolbar, Typography, useMediaQuery, useTheme } from '@mui/material';
import Link from 'next/link';
import { PropsWithChildren, useCallback, useState } from 'react';
import { isAdminSelector, isAuthenticatedSelector, logoutThunk } from '../store/authSlice';
import { RootState, useAppDispatch, useAppSelector } from '../store/store';
import MenuIcon from '@mui/icons-material/Menu';
import EventNoteIcon from '@mui/icons-material/EventNote';

import FinancialIcon from '@mui/icons-material/AccountBalanceWallet';
import StatsIcon from '@mui/icons-material/BarChart';
import UsersIcon from '@mui/icons-material/Group';
import PublisherMapIcon from '@mui/icons-material/Map';
import MissingStatsIcon from '@mui/icons-material/ReportProblem';
import PerformanceIcon from '@mui/icons-material/ShowChart';
import { CallMade, RequestQuote } from '@mui/icons-material';
import OffersIcon from '@mui/icons-material/AdsClick';
import DataSourceIcon from '@mui/icons-material/Storage'; // New import for Data Sources icon
import { SnackbarProvider } from 'notistack'
import { ConfirmProvider } from 'material-ui-confirm';

export default function SidebarView({ children }: PropsWithChildren) {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [open, setOpen] = useState(!isMobile);

  const drawerWidth = 240;
  const isAuthenticated = useAppSelector((state: RootState) => isAuthenticatedSelector(state.auth));
  const isAdmin = useAppSelector(isAdminSelector);

  const toggleDrawer = useCallback(() => {
    setOpen(!open);
  }, [open]);

  const logout = useCallback(() => {
    dispatch(logoutThunk())
  }, [dispatch]);


  const links = [
    { href: '/stats', text: 'Stats', icon: <StatsIcon />, visible: isAuthenticated },
    { href: '/performance-details', text: 'Performance Details', icon: <PerformanceIcon />, visible: isAdmin },
    { href: '/financial', text: 'Financial Report', icon: <FinancialIcon />, visible: isAdmin },
    { href: '/missing-stats', text: 'Missing Stats', icon: <MissingStatsIcon />, visible: isAdmin },
    { href: '/publisher-map', text: 'Publisher Map', icon: <PublisherMapIcon />, visible: isAdmin },
    { href: '/publisher-payments', text: 'Publisher Payment', icon: <RequestQuote />, visible: isAdmin },
    { href: '/payments', text: 'Payments', icon: <RequestQuote />, visible: isAdmin },
    { href: '/feed-payments', text: 'Feed Payment', icon: <RequestQuote />, visible: isAdmin },
    { href: '/feeds', text: 'Feeds', icon: <OffersIcon />, visible: isAdmin },
    { href: '/data-sources', text: 'Data Sources', icon: <DataSourceIcon />, visible: isAdmin }, // New Data Sources route
    { href: '/users', text: 'Users', icon: <UsersIcon />, visible: isAdmin },
    { href: '/redirect-checker', text: 'Redirect Checker', icon: <CallMade />, visible: isAdmin }, // Updated icon here
    { href: '/logs', text: 'Logs', icon: <EventNoteIcon />, visible: isAdmin },
  ];

  return (
    <ConfirmProvider>
      <SnackbarProvider>
        <Box sx={{ display: 'flex' }}>
          <AppBar position="fixed" sx={{ display: { md: 'none' } }}>
            <Toolbar>
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={toggleDrawer}
                sx={{ mr: 2 }}
              >
                <MenuIcon />
              </IconButton>
              <Typography variant="h6" noWrap>
                Algorise
              </Typography>
            </Toolbar>
          </AppBar>

          <Drawer
            variant={isMobile ? 'temporary' : 'persistent'}
            open={open}
            onClose={toggleDrawer}
            sx={{
              width: drawerWidth,
              flexShrink: 0,
              '& .MuiDrawer-paper': {
                width: drawerWidth,
                boxSizing: 'border-box',
              },
              ...(isMobile && {
                '.MuiDrawer-paper': { top: '56px' }
              })
            }}
          >
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              color: 'white',
              background: "linear-gradient(to bottom, #4b6cb7, #182848);",

              svg: {
                fill: 'white'
              }
            }}>
              <Typography variant="h5" sx={{
                p: 2,
                textAlign: 'center',
                fontFamily: 'Poppins'
              }}>Algorise</Typography>
              <Divider sx={{ color: "#fff" }} />
              <List sx={{ color: 'white', textDecoration: 'none', 'a:link, a:visited': { textDecoration: 'none', color: 'white' }, py: 0, my: 5 }}>
                {links.map(({ href, text, icon, visible }) => visible && (
                  <ListItemButton component={Link} href={href} key={href}>
                    <ListItemIcon>
                      {icon}
                    </ListItemIcon>
                    <ListItemText primary={text} />
                  </ListItemButton>
                ))}
              </List>
              <Box sx={{ flexGrow: 1 }} />
              {isAuthenticated && <List>
                <ListItemButton onClick={logout}>
                  <ListItemIcon>
                    <ExitToAppIcon />
                  </ListItemIcon>
                  <ListItemText primary="Logout" />
                </ListItemButton>
              </List>}
            </Box>
          </Drawer>

          <Box component="main" sx={{ flexGrow: 1, p: '0 10px' }}>
            <Toolbar sx={{ display: { md: 'none' } }} />
            {children}
          </Box>
        </Box>
      </SnackbarProvider>
    </ConfirmProvider>
  );
}